import { EventMeta } from '../../../interfaces/meta.interface';

type PageViewedFields = {
    ObjectId: string;
};

const pageViewed: EventMeta<PageViewedFields> = {
    title: 'SharePoint Page Viewed',
    default: {
        description: 'SharePoint Page Viewed',
        longDescription: 'A page was viewed on a OneDrive site.',
        severity: 'info',
    },
    fieldsExtractor: (log: any) => {
        return {
            ObjectId: log.ObjectId,
        };
    },
    classifications: [
        // page viewed. fraudulent IP. not in allowed list
        {
            severity: 'danger',
            description: (fields) => `A suspicious IP viewed a OneDrive page`,
            longDescription: (fields) =>
                `A page was viewed on a OneDrive site. The action originated from a suspicious IP that is included in your allowed IP list. While the IP address belongs to an approved range, it has been flagged as suspicious. This may represent opportunistic or anomalous access and should be reviewed, especially if repeated or linked to sensitive content.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // page viewed. user in org. fraudulent IP, allowed IP
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed IP list viewed a page`,
            longDescription: (fields) =>
                `A page was viewed on a OneDrive site. The action originated from a suspicious IP that is included in your allowed IP list. While the IP address belongs to an approved range, it has been flagged as suspicious. This may represent opportunistic or anomalous access and should be reviewed, especially if repeated or linked to sensitive content.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // page viewed. user not in org. fraudulent IP, allowed IP
        {
            severity: 'info',
            description: (fields) =>
                `A suspicious IP within your allowed IP list used a non-org account to view a page`,
            longDescription: (fields) =>
                `A page was viewed on a OneDrive site with an account that does not belong to your organisation. The action originated from a suspicious IP included in your allowed IP list. Although the IP range is normally trusted, its suspicious status combined with the use of an external account makes this event unusual. Review the access to confirm whether it was expected or potentially unauthorized.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // page viewed. user in org. fraudulent IP, in allowed region.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed region viewed a page`,
            longDescription: (fields) =>
                `A page was viewed on a OneDrive site. The action originated from a suspicious IP located in your allowed region. Although the geographic origin is normally trusted, the IP address itself has been flagged as suspicious. This does not necessarily indicate malicious behaviour, but the mismatch between a trusted region and a flagged IP makes the event unusual. Review the activity to confirm whether the access was expected.`,
            predicates: {
                isAllowedRegion: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // page viewed. user not in org. fraudulent IP, in allowed region.
        {
            severity: 'info',
            description: (fields) => `A suspicious IP within your allowed region used a non-org account to view a page`,
            longDescription: (fields) =>
                `A page was viewed on a OneDrive site with an account that does not belong to your organisation. The action originated from a suspicious IP in your allowed region. While the geographic origin is normally considered trusted, the IP being flagged as suspicious combined with the use of an external account makes the event unusual. Review the activity to determine if the access was expected or potentially unauthorized.`,
            predicates: {
                isAllowedRegion: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // page viewed. user in org. fraudulent IP, in allowed countries.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed countries viewed a page`,
            longDescription: (fields) =>
                `A page was viewed on a OneDrive site. The action originated from a suspicious IP located in one of your allowed countries. While the geographic origin aligns with your access policies, the IP address itself is flagged as suspicious. This does not necessarily indicate malicious behaviour but should be reviewed, particularly if there are repeated page views or access to sensitive content from similar flagged sources.`,
            predicates: {
                isAllowedCountry: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // page viewed. user not in org. fraudulent IP, in allowed countries.
        {
            severity: 'info',
            description: (fields) =>
                `A suspicious IP within your allowed countries used a non-org account to view a page`,
            longDescription: (fields) =>
                `A page was viewed on a OneDrive site with an account that does not belong to your organisation. The action originated from a suspicious IP located in one of your allowed countries. While the country itself aligns with your access policies, the IP being flagged as suspicious and the use of an external account make this activity unusual. Review the event to confirm whether the access was expected or potentially unauthorized.`,
            predicates: {
                isAllowedCountry: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // page viewed. user in org. fraudulent IP, in allowed asn.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed ASN list viewed a page`,
            longDescription: (fields) =>
                `A page was viewed on a OneDrive site. The action originated from a suspicious IP that belongs to an ASN you allow. Although the ASN itself is trusted, the individual IP has been flagged as suspicious. The mix of a trusted ASN and a flagged IP is unusual. Review the access to determine whether it was expected or potentially unauthorized.`,
            predicates: {
                isAllowedASN: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // page viewed. user not in org. fraudulent IP, in allowed asn.
        {
            severity: 'info',
            description: (fields) =>
                `A suspicious IP within your allowed ASN list used a non-org account to view a page`,
            longDescription: (fields) =>
                `A page was viewed on a OneDrive site with an account that does not belong to your organisation. The action originated from a suspicious IP included in your allowed ASN list. While the ASN itself is trusted, the specific IP has been flagged as suspicious, and the use of an external account makes this event unusual. Review the activity to determine whether the access was expected or potentially unauthorized, especially if sensitive resources were involved.`,
            predicates: {
                isAllowedASN: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // page viewed. user in org. not in allowed list
        {
            severity: 'warning',
            description: (fields) => `An unapproved IP viewed a page`,
            longDescription: (fields) =>
                `A page was viewed on a OneDrive site. The action originated from an unapproved IP that is not in your allowed list. While page views on OneDrive are routine, activity from outside your approved IPs is unusual and may indicate anomalous or unauthorized access. Review the details of the access to confirm whether it was expected or potentially inappropriate.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // page viewed. user not in org. not in allowed list
        {
            severity: 'info',
            description: (fields) => `An unapproved IP used a non-org account to view a page`,
            longDescription: (fields) =>
                `A page was viewed on a OneDrive site with an account that does not belong to your organisation. The action originated from an unapproved IP that is not in your allowed list. While OneDrive access is common, the combination of an external account and an unapproved IP is unusual and may indicate unauthorized activity. Review the activity to confirm whether the access was legitimate.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // page viewed. microsoft activity
        {
            severity: 'info',
            description: (fields) => `A Microsoft system operation (synchronization server IP) viewed a page`,
            longDescription: (fields) =>
                `A page was viewed on a OneDrive site. This was recorded as a Microsoft system operation event, with the IP address tied to the Microsoft server responsible for synchronization. This entry is informational and reflects expected background activity. No action is required unless page views attributed to Microsoft system processes appear inconsistent with your configuration or expected patterns.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        // page viewed.
        {
            severity: 'info',
            description: (fields) => `A OneDrive page was viewed`,
            longDescription: (fields) =>
                `A page was viewed on a OneDrive site. This is an informational record of standard site activity. No unusual conditions were noted with this entry. No immediate action is required unless the page view was unexpected or coincides with other anomalies.`,
        },
    ],
};

export default pageViewed;
